/**
 * The nudge that survives the wizard (#2493).
 *
 * `completedAt` used to silence this for good, and `finishRemaining` sets it
 * while marking untouched steps done — so clicking through an empty wizard
 * bought permanent silence and no route back: the prompt was the only way in,
 * and once gone, only a hand-typed /onboarding URL reached it.
 *
 * Two questions, two sources. `completedAt` answers "should we still offer the
 * wizard"; the readiness rules answer "does this workshop actually work". The
 * prompt changes shape rather than disappearing:
 *
 *   before the wizard  → offer the wizard, as before
 *   after, gaps remain → name them and point at settings, not the wizard
 *                        (it has been seen; sending someone back is wrong)
 *   after, all good    → silent
 *
 * Dismissal and the 24 h snooze are unchanged: this argues about *whether*
 * there is something to say, never about the workshop's right to postpone it.
 */

import { useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useWorkshopReadiness } from '@/features/settings/useWorkshopReadiness';
import { useOnboarding } from './useOnboarding';
import { toWorkshopSettings } from './toWorkshopSettings';

export function OnboardingPrompt() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const location = useLocation();
  const { shouldPrompt, isComplete, canManage } = useOnboarding();
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const { tenantName, tenantSettings, isFetched } = useTenantSettings();
  const settings = toWorkshopSettings(tenantName, (tenantSettings ?? {}) as Record<string, unknown>);
  const { issues, isLoading } = useWorkshopReadiness(settings);

  // After the wizard the prompt is driven by data, not by the step list.
  // Waiting for both fetches keeps it from flashing at a workshop that is
  // actually fine.
  const gapsAfterWizard = isComplete && canManage && isFetched && !isLoading && issues.length > 0;

  if (dismissedThisSession || location.pathname.startsWith('/onboarding')) return null;
  if (!shouldPrompt && !gapsAfterWizard) return null;

  const action = () => {
    // Settings once the wizard is behind us: every remaining gap is fixed
    // there, and several are on tabs the wizard never covered.
    void navigate(gapsAfterWizard ? '/settings' : '/onboarding');
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity={gapsAfterWizard ? 'warning' : 'info'}
        action={
          <>
            <Button color="inherit" size="small" onClick={action}>
              {gapsAfterWizard ? t('prompt.fixAction') : t('prompt.action')}
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => setDismissedThisSession(true)}
            >
              {t('prompt.dismiss')}
            </Button>
          </>
        }
      >
        {gapsAfterWizard
          ? t('prompt.gapsTitle', { count: issues.length })
          : t('prompt.title')}
      </Alert>
    </Box>
  );
}
